import React, { useState, useEffect, useRef } from 'react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { toast } from '../components/ui/use-toast';
import { useNavigate, useLocation } from 'react-router-dom';
import { Check, X, Upload, Search, ArrowRight, Trash2, Pause, ImageIcon } from 'lucide-react';
import { Loader2 } from 'lucide-react';
import { useBackNavigation } from '../hooks/use-back-navigation';
import { getAuthToken } from '@/lib/authToken';
import { getBaseUrl } from '@/lib/api';

interface ImageItem {
  id: string;
  url: string;
  name: string;
  isSelected: boolean;
  isPrimary: boolean;
  isLocal: boolean;
  file?: File;
  serverUrl?: string; // Added for server URL reference
  imageurl?: string; // API field for submission
  localPreview?: string; // Local file preview as fallback
}

interface ProductImagesRequest {
  ProductCode: number;
  images: {
    imageurl: string;
    isPrimary: number;
  }[];
}

interface ProductImagesResponse {
  Header: string[];
  EditableColumns: string[];
  ValueChangeColumns: string[];
  TwoFields: string[];
  status: number;
  message: string;
  data: any[];
  TotalRecords: number;
}

interface ProductImageData {
  ProductImageId: number;
  ProductCode: number;
  isPrimary: boolean;
  ImageId: number;
  ImageUrl: string;
}

interface ViewImagesResponse {
  Header: string[];
  EditableColumns: string[];
  ValueChangeColumns: string[];
  TwoFields: string[];
  status: number;
  message: string;
  data: ProductImageData[];
  TotalRecords: number;
}

const UploadImage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const productCodeFromQuery = searchParams.get('code')
    ? Number(searchParams.get('code'))
    : undefined;
  const productName = location.state?.productName || '';
  const productCode = location.state?.productCode ?? productCodeFromQuery;
  const token = location.state?.token as string | undefined;

  // Helper: always return user to Inventory (Transactions → Inventory subpage)
  const navigateBackToInventory = () => {
    // Persist intent so Dashboard/TransactionsTab can reopen Inventory
    sessionStorage.setItem('openInventory', 'true');
    navigate('/', { replace: true });
  };

  // Custom back navigation handler for UploadImage
  const handleUploadImageBack = () => {
    navigateBackToInventory();
  };

  // Use back navigation hook with custom handler
  // Note: This page is accessed via direct route, so it handles its own back navigation
  // When accessed from Dashboard, it should show exit confirmation instead of navigating back
  useBackNavigation({
    onBack: handleUploadImageBack,
    canGoBack: true,
    showExitConfirmation: true // Show exit confirmation when accessed from Dashboard
  });

  // Toggle state
  const [activeSection, setActiveSection] = useState<'online' | 'offline'>('online');

  // Online section state
  const [searchTerm, setSearchTerm] = useState(productName);
  const [isSearching, setIsSearching] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);
  const [searchResults, setSearchResults] = useState<string[]>([]);
  const [selectedOnlineImages, setSelectedOnlineImages] = useState<string[]>([]);
  const [primaryOnlineImage, setPrimaryOnlineImage] = useState<string | null>(null);
  
  // Progress tracking for streaming
  const [filterProgress, setFilterProgress] = useState({
    processed: 0,
    total: 0,
    compatible: 0
  });
  
  // Stop processing flag
  const [shouldStopProcessing, setShouldStopProcessing] = useState(false);
  
  // Store remaining URLs for resuming
  const [remainingUrls, setRemainingUrls] = useState<string[]>([]);
  
  // Ref for immediate stop checking (more responsive than state)
  const stopProcessingRef = useRef(false);
  
  // Ref to track if "Found Compatible Images" notification has been shown
  const hasShownCompatibleNotification = useRef(false);

  // Offline section state
  const [localFile, setLocalFile] = useState<File | null>(null);
  const [localFilePreview, setLocalFilePreview] = useState<string | null>(null);
  const [offlineImages, setOfflineImages] = useState<ImageItem[]>([]);
  const [primaryOfflineImage, setPrimaryOfflineImage] = useState<string | null>(null);
  const [showDeleteCheckboxes, setShowDeleteCheckboxes] = useState(false);
  const [imagesToDelete, setImagesToDelete] = useState<string[]>([]);
  const [showMobileSubmitBar, setShowMobileSubmitBar] = useState(false);
  const [showOfflineDeleteConfirm, setShowOfflineDeleteConfirm] = useState(false);
  const [offlineDeleteId, setOfflineDeleteId] = useState<string | null>(null);

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // View popup state
  const [showViewPopup, setShowViewPopup] = useState(false);
  const [viewImages, setViewImages] = useState<ProductImageData[]>([]);
  const [isLoadingView, setIsLoadingView] = useState(false);
  const [viewError, setViewError] = useState<string | null>(null);

  // Delete confirmation popup state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<ProductImageData | null>(null);

  // Loading states for individual images and upload
  const [loadingImages, setLoadingImages] = useState<Set<string>>(new Set());
  const [isUploading, setIsUploading] = useState(false);

  // Responsive state
  const [screenSize, setScreenSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768
  });

  const baseUrl = getBaseUrl();
  // Prefer token from navigation state, but fall back to global auth token
  const stateAuthToken =
    token && token.length > 0
      ? (token.startsWith('Bearer ') ? token : `Bearer ${token}`)
      : '';
  const authToken = stateAuthToken || getAuthToken();

  // Guard against missing product code (e.g., direct navigation or page refresh)
  useEffect(() => {
    if (!productCode) {
      toast({
        title: 'Error',
        description: 'Product code is missing. Please open image upload from the Inventory page.',
        variant: 'destructive',
      });
    }
  }, [productCode]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get responsive grid classes based on current screen size
  const getGridClasses = () => {
    if (screenSize.width <= 480) {
      return 'grid-cols-1'; // Very small devices
    } else if (screenSize.width <= 768) {
      return 'grid-cols-2'; // Mobile phones and tablets
    } else {
      return 'grid-cols-3'; // Large screens
    }
  };

  // Get responsive search layout
  const getSearchLayout = () => {
    if (screenSize.width <= 768) {
      return 'flex-col gap-2'; // Stacked on mobile
    } else {
      return 'flex-row gap-2'; // Side by side on desktop
    }
  };

  // Handle primary image selection in online section
  const handlePrimaryOnlineChange = (imageUrl: string) => {
    if (primaryOnlineImage === imageUrl) {
      // Uncheck if already selected
      setPrimaryOnlineImage(null);
      toast({ 
        title: 'Primary Image Removed', 
        description: 'Primary image selection has been removed', 
        variant: 'default' 
      });
    } else {
      // Set new primary image
      const previousPrimary = primaryOnlineImage;
      setPrimaryOnlineImage(imageUrl);
      
      if (previousPrimary) {
        toast({ 
          title: 'Primary Image Changed', 
          description: 'Primary image selection has been updated', 
          variant: 'default' 
        });
      } else {
        toast({ 
          title: 'Primary Image Set', 
          description: 'Primary image has been selected', 
          variant: 'default' 
        });
      }
    }
  };

  // Fetch product images for View popup
  const fetchProductImages = async () => {
    if (!productCode) {
      toast({
        title: 'Error',
        description: 'Product code not available',
        variant: 'destructive'
      });
      return;
    }

    setIsLoadingView(true);
    setViewError(null);

    try {
      const response = await fetch(`${baseUrl}/api/ProductImages?ProductCode=${productCode}`, {
        method: 'GET',
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json'
        }
      });

      const data: ViewImagesResponse = await response.json();
      console.log('View images API response:', data);

      if (data.status === 0) {
        setViewImages(data.data || []);
        setShowViewPopup(true);
      } else {
        setViewError(data.message || 'Failed to fetch product images');
        toast({
          title: 'Error',
          description: data.message || 'Failed to fetch product images',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error fetching product images:', error);
      const errorMessage = error instanceof Error ? error.message : 'Network error occurred';
      setViewError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setIsLoadingView(false);
    }
  };

  // Handle Update action for image in popup (Update Primary)
  const handleUpdateImage = async (imageData: ProductImageData) => {
    if (!productCode) {
      toast({
        title: 'Error',
        description: 'Product code not available',
        variant: 'destructive'
      });
      return;
    }

    try {
      const response = await fetch(`${baseUrl}/api/ProductImages/UpdatePrimary`, {
        method: 'PUT',
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ProductCode: productCode,
          ImageId: imageData.ImageId
        })
      });

      const data = await response.json();
      console.log('Update primary API response:', data);

      if (data.status === 0) {
        toast({
          title: 'Success',
          description: data.message || 'Primary image updated successfully',
          variant: 'default'
        });
        
        // Refresh the images list to reflect the changes
        await fetchProductImages();
      } else {
        toast({
          title: 'Error',
          description: data.message || 'Failed to update primary image',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error updating primary image:', error);
      const errorMessage = error instanceof Error ? error.message : 'Network error occurred';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
    }
  };

  // Handle Delete action for image in popup
  const handleDeleteImage = (imageData: ProductImageData) => {
    setImageToDelete(imageData);
    setShowDeleteConfirm(true);
  };

  // Confirm delete action
  const confirmDelete = async () => {
    if (!imageToDelete || !productCode) {
      toast({
        title: 'Error',
        description: 'Product code or image data not available',
        variant: 'destructive'
      });
      return;
    }

    try {
      const response = await fetch(`${baseUrl}/api/ProductImages/Delete`, {
        method: 'DELETE',
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ProductCode: productCode,
          ImageId: imageToDelete.ImageId
        })
      });

      const data = await response.json();
      console.log('Delete image API response:', data);

      if (data.status === 0) {
        toast({
          title: 'Success',
          description: data.message || 'Image deleted successfully',
          variant: 'default'
        });
        
        // Refresh the images list to reflect the changes
        await fetchProductImages();
      } else {
        toast({
          title: 'Error',
          description: data.message || 'Failed to delete image',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      const errorMessage = error instanceof Error ? error.message : 'Network error occurred';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      // Close the confirmation popup
      setShowDeleteConfirm(false);
      setImageToDelete(null);
    }
  };

  // Cancel delete action
  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setImageToDelete(null);
  };

  // Handle primary image selection in offline section
  const handlePrimaryOfflineChange = (imageId: string) => {
    if (primaryOfflineImage === imageId) {
      // Uncheck if already selected
      setPrimaryOfflineImage(null);
      setShowMobileSubmitBar(false);
      toast({ 
        title: 'Primary Image Removed', 
        description: 'Primary image selection has been removed', 
        variant: 'default' 
      });
    } else {
      // Set new primary image and show message
      const previousPrimary = primaryOfflineImage;
      setPrimaryOfflineImage(imageId);
      setShowMobileSubmitBar(true);
      
      if (previousPrimary) {
        toast({ 
          title: 'Primary Image Changed', 
          description: 'Primary image selection has been updated', 
          variant: 'default' 
        });
      } else {
        toast({ 
          title: 'Primary Image Set', 
          description: 'Primary image has been selected', 
          variant: 'default' 
        });
      }
    }
  };

  // Update offline images when primary selection changes
  useEffect(() => {
    setOfflineImages(prev => prev.map(img => ({
      ...img,
      isPrimary: img.id === primaryOfflineImage
    })));
  }, [primaryOfflineImage]);

  // Sync primary selection from online to offline when moving images
  const syncPrimaryFromOnlineToOffline = (newImages: ImageItem[]) => {
    if (primaryOnlineImage) {
      // Find the corresponding offline image
      const primaryOfflineImage = newImages.find(img => img.url === primaryOnlineImage);
      if (primaryOfflineImage) {
        setPrimaryOfflineImage(primaryOfflineImage.id);
        return newImages.map(img => ({
          ...img,
          isPrimary: img.id === primaryOfflineImage.id
        }));
      }
    }
    return newImages;
  };

  // Comprehensive test function to try different approaches
  const testMultipleApproaches = async (testUrl: string) => {
    try {
      console.log('🧪🧪🧪 COMPREHENSIVE API TESTING 🧪🧪🧪');
      console.log('Testing URL:', testUrl);
      
      // Fetch the image
      const imageResponse = await fetch(testUrl);
      if (!imageResponse.ok) {
        throw new Error(`Failed to fetch image: ${imageResponse.status}`);
      }

      const imageBlob = await imageResponse.blob();
      const cleanFileName = testUrl.split('/').pop()?.split('?')[0] || 'image.jpg';
      const file = new File([imageBlob], cleanFileName, { type: imageBlob.type });
      
      console.log('File prepared:', file.name, file.size, 'bytes');

      // Test different approaches
      const approaches = [
        { name: 'file field only', data: () => { const fd = new FormData(); fd.append('file', file); return fd; }},
        { name: 'url field only', data: () => { const fd = new FormData(); fd.append('url', testUrl); return fd; }},
        { name: 'both file and url', data: () => { const fd = new FormData(); fd.append('file', file); fd.append('url', testUrl); return fd; }},
        { name: 'image field', data: () => { const fd = new FormData(); fd.append('image', file); return fd; }},
        { name: 'upload field', data: () => { const fd = new FormData(); fd.append('upload', file); return fd; }},
        { name: 'photo field', data: () => { const fd = new FormData(); fd.append('photo', file); return fd; }},
      ];

      for (const approach of approaches) {
        try {
          console.log(`\n🧪 Testing approach: ${approach.name}`);
          
          const formData = approach.data();
          
          // Log what we're sending
          console.log('FormData contents:');
          for (const [key, value] of formData.entries()) {
            if (value instanceof File) {
              console.log(`  ${key}: File(${value.name}, ${value.size} bytes)`);
            } else {
              console.log(`  ${key}: ${value}`);
            }
          }
          
          const response = await fetch(`${baseUrl}/api/getWebURL`, {
            method: 'POST',
            headers: {
              'Authorization': authToken,
            },
            body: formData
          });

          console.log(`  Status: ${response.status}`);
          
          if (response.ok) {
            const data = await response.json();
            console.log(`  Response:`, data);
            
            if (data.url && data.url !== '') {
              console.log(`  ✅ SUCCESS! ${approach.name} worked!`);
              console.log(`  ✅ URL: ${data.url}`);
              return { success: true, approach: approach.name, url: data.url };
            } else {
              console.log(`  ❌ Empty URL returned`);
            }
          } else {
            const errorText = await response.text();
            console.log(`  ❌ Error: ${response.status} - ${errorText}`);
          }
        } catch (error) {
          console.log(`  ❌ Exception: ${error}`);
        }
      }
      
      console.log('\n🧪🧪🧪 ALL APPROACHES FAILED 🧪🧪🧪');
      return { success: false, approach: 'none', url: null };
      
    } catch (error) {
      console.error('❌ Comprehensive test failed:', error);
      return { success: false, approach: 'error', url: null };
    }
  };

  // Test getWebURL API function for debugging
  const testGetWebURL = async (testUrl: string) => {
    try {
      console.log('🧪 Testing getWebURL API with URL:', testUrl);
      
      console.log('🧪 Fetching image from URL to convert to File object...');
      
      // Fetch the image from the URL (like offline does)
      const imageResponse = await fetch(testUrl);
      if (!imageResponse.ok) {
        throw new Error(`Failed to fetch image: ${imageResponse.status}`);
      }

      // Get the image blob (like offline does)
      const imageBlob = await imageResponse.blob();
      console.log('🧪 Image blob fetched:', imageBlob.size, 'bytes');

      // Convert blob to File object (like offline does)
      const fileName = testUrl.split('/').pop() || 'image.jpg';
      const file = new File([imageBlob], fileName, { type: imageBlob.type });
      console.log('🧪 File object created:', file.name, file.size, 'bytes');

      // Create FormData with file (exactly like offline does)
      const formData = new FormData();
      formData.append('file', file); // This matches offline: formData.append('file', file)

      // Log FormData contents for debugging
      console.log('🧪 === FORM DATA CONTENTS ===');
      for (const [key, value] of formData.entries()) {
        if (value instanceof File) {
          console.log(`🧪 ${key}:`, {
            name: value.name,
            size: value.size,
            type: value.type,
            lastModified: value.lastModified
          });
        } else {
          console.log(`🧪 ${key}:`, value);
        }
      }
      console.log('🧪 === END FORM DATA ===');

      console.log('🧪 Sending FormData to getWebURL API with file:', file.name);
      
      const response = await fetch(`${baseUrl}/api/getWebURL`, {
        method: 'POST',
        headers: {
          'Authorization': authToken,
        },
        body: formData
      });

      console.log('🧪 Response status:', response.status);
      console.log('🧪 Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🧪 API Error Response:', errorText);
        throw new Error(`getWebURL failed: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('✅ API Response Data:', data);
      console.log('✅ Response URL:', data.url);
      
      return data;
    } catch (error) {
      console.error('❌ Test failed:', error);
      return null;
    }
  };

  // Test image compatibility before upload
  const testImageCompatibility = (imageBlob: Blob, imageUrl: string) => {
    const issues = [];
    
    // Check file size - STRICTER LIMIT
    const maxSize = 2 * 1024 * 1024; // 2MB (reduced from 5MB)
    if (imageBlob.size > maxSize) {
      issues.push(`Large file size (${(imageBlob.size / 1024 / 1024).toFixed(2)}MB)`);
    }
    
    // Check file type
    const supportedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!supportedTypes.includes(imageBlob.type)) {
      issues.push(`Unsupported file type: ${imageBlob.type}`);
    }
    
    // Check URL format
    if (imageUrl.includes('?')) {
      issues.push('URL contains query parameters (may cause issues)');
    }
    
    // Check for known problematic domains
    const problematicDomains = ['tiktok.com', 'instagram.com', 'facebook.com'];
    const domain = new URL(imageUrl).hostname;
    if (problematicDomains.some(d => domain.includes(d))) {
      issues.push(`Image from ${domain} (may have CORS restrictions)`);
    }
    
    return {
      isCompatible: issues.length === 0,
      issues,
      warnings: issues.length > 0 ? `Potential issues: ${issues.join(', ')}` : null
    };
  };

  // Function to handle when a compatible image is found
  const handleImageFound = (imageUrl: string) => {
    setSearchResults(prev => [...prev, imageUrl]);
    
    // Show notification only once when first compatible image is found
    if (!hasShownCompatibleNotification.current) {
      hasShownCompatibleNotification.current = true;
      toast({ 
        title: 'Found Compatible Images', 
        description: 'Images are being loaded as they\'re verified...', 
        variant: 'default' 
      });
    }
  };

  // Resume processing from a specific point
  const resumeProcessing = async (remainingUrls: string[]) => {
    console.log('🔄 Resuming processing...');
    setShouldStopProcessing(false);
    stopProcessingRef.current = false;
    setIsFiltering(true);
    
    const additionalCount = await streamCompatibleImages(remainingUrls, handleImageFound);
    
    setIsFiltering(false);
    
    if (additionalCount > 0) {
      toast({ 
        title: 'Processing Complete', 
        description: `Found ${additionalCount} additional compatible images`, 
        variant: 'default' 
      });
    }
  };

  // Stream-compatible images as they're found instead of waiting for all
  const streamCompatibleImages = async (imageUrls: string[], onImageFound: (imageUrl: string) => void) => {
    console.log('🔍 Streaming images for compatibility...');
    let processedCount = 0;
    let compatibleCount = 0;
    
    // Reset stop flags
    setShouldStopProcessing(false);
    stopProcessingRef.current = false;
    
    // Initialize progress
    setFilterProgress({
      processed: 0,
      total: imageUrls.length,
      compatible: 0
    });
    
    // Process images in batches for better UX
    const batchSize = 3; // Reduced from 5 for faster initial results
    const totalImages = imageUrls.length;
    
    for (let i = 0; i < imageUrls.length; i += batchSize) {
      // Check if user wants to stop processing (use ref for immediate response)
      if (stopProcessingRef.current) {
        console.log('🛑 Processing stopped by user');
        // Store remaining URLs for potential resume
        setRemainingUrls(imageUrls.slice(i));
        break;
      }
      
      const batch = imageUrls.slice(i, i + batchSize);
      
      // Process batch in parallel
      const batchPromises = batch.map(async (imageUrl) => {
        try {
          processedCount++;
          
          // Check stop flag before starting image processing (use ref)
          if (stopProcessingRef.current) {
            console.log('🛑 Stopping individual image processing');
            return null;
          }
          
          // Quick compatibility check without downloading
          const url = new URL(imageUrl);
          const domain = url.hostname;
          
          // Skip known problematic domains
          const problematicDomains = [
            'tiktok.com', 'instagram.com', 'facebook.com', 'twitter.com', 
            'x.com', 'snapchat.com', 'pinterest.com', 'reddit.com'
          ];
          
          if (problematicDomains.some(d => domain.includes(d))) {
            console.log(`❌ Skipping ${domain} - known CORS issues`);
            return null;
          }
          
          // Skip URLs with query parameters (often cause issues)
          if (imageUrl.includes('?')) {
            console.log(`❌ Skipping URL with query parameters`);
            return null;
          }
          
          // Check stop flag before HEAD request (use ref)
          if (stopProcessingRef.current) {
            console.log('🛑 Stopping before HEAD request');
            return null;
          }
          
          // Test if image is accessible and reasonable size
          const response = await fetch(imageUrl, { 
            method: 'HEAD', // Only get headers, not the full image
            mode: 'cors'
          });
          
          if (!response.ok) {
            console.log(`❌ Image not accessible: ${response.status}`);
            return null;
          }
          
          // Check content type
          const contentType = response.headers.get('content-type');
          if (!contentType || !contentType.startsWith('image/')) {
            console.log(`❌ Not an image: ${contentType}`);
            return null;
          }
          
          // Check file size (if available) - STRICTER LIMIT
          const contentLength = response.headers.get('content-length');
          if (contentLength) {
            const sizeInMB = parseInt(contentLength) / (1024 * 1024);
            if (sizeInMB > 2) { // Reduced from 5MB to 2MB
              console.log(`❌ Image too large: ${sizeInMB.toFixed(2)}MB`);
              return null;
            }
          }
          
          // Check stop flag before full image fetch (use ref)
          if (stopProcessingRef.current) {
            console.log('🛑 Stopping before full image fetch');
            return null;
          }
          
          // CRITICAL: Test with actual API call to ensure it works
          console.log(`🧪 Testing API compatibility for: ${imageUrl.substring(0, 50)}...`);
          
          // Fetch the image
          const imageResponse = await fetch(imageUrl, {
            method: 'GET',
            mode: 'cors'
          });
          
          if (!imageResponse.ok) {
            console.log(`❌ Failed to fetch image: ${imageResponse.status}`);
            return null;
          }
          
          const imageBlob = await imageResponse.blob();
          
          // Check actual file size from blob
          const actualSizeInMB = imageBlob.size / (1024 * 1024);
          if (actualSizeInMB > 2) { // 2MB limit
            console.log(`❌ Actual image too large: ${actualSizeInMB.toFixed(2)}MB`);
            return null;
          }
          
          // Check stop flag before API call (use ref)
          if (stopProcessingRef.current) {
            console.log('🛑 Stopping before API call');
            return null;
          }
          
          // Create file object
          const fileName = imageUrl.split('/').pop()?.split('?')[0] || 'image.jpg';
          const file = new File([imageBlob], fileName, { type: imageBlob.type });
          
          // Test with actual API call (single attempt)
          const formData = new FormData();
          formData.append('file', file);
          
          console.log(`🚀 Testing API call for: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`);
          
          const apiResponse = await fetch(`${baseUrl}/api/getWebURL`, {
            method: 'POST',
            headers: {
              'Authorization': authToken,
            },
            body: formData
          });
          
          if (!apiResponse.ok) {
            console.log(`❌ API test failed: ${apiResponse.status}`);
            return null;
          }
          
          const apiData = await apiResponse.json();
          
          if (!apiData.url || apiData.url === '') {
            console.log(`❌ API returned empty URL`);
            return null;
          }
          
          console.log(`✅ Image fully compatible: ${imageUrl.substring(0, 50)}...`);
          console.log(`✅ API returned URL: ${apiData.url}`);
          return imageUrl;
          
        } catch (error) {
          console.log(`❌ Error testing image: ${error.message}`);
          return null;
        }
      });
      
      // Wait for batch to complete
      const batchResults = await Promise.all(batchPromises);
      
      // Add compatible images to results immediately
      batchResults.forEach(result => {
        if (result) {
          compatibleCount++;
          onImageFound(result);
        }
      });
      
      // Update progress state
      setFilterProgress({
        processed: processedCount,
        total: totalImages,
        compatible: compatibleCount
      });
      
      // Update progress every batch
      console.log(`📊 Progress: ${processedCount}/${totalImages} processed, ${compatibleCount} compatible found`);
      
      // Check stop flag again before continuing to next batch
      if (stopProcessingRef.current) {
        console.log('🛑 Processing stopped between batches');
        setRemainingUrls(imageUrls.slice(i + batchSize));
        break;
      }
      
      // Small delay to prevent overwhelming the UI and allow stop checks
      await new Promise(resolve => setTimeout(resolve, 50)); // Reduced from 100ms
    }
    
    const finalMessage = shouldStopProcessing ? 'Processing stopped early' : 'Streaming complete';
    console.log(`🔍 ${finalMessage}: ${compatibleCount}/${totalImages} images compatible`);
    return compatibleCount;
  };

  // Stop search function
  const handleStopSearch = () => {
    setIsSearching(false);
    setIsFiltering(false);
    setShouldStopProcessing(true);
    stopProcessingRef.current = true;
    toast({ 
      title: 'Search Stopped', 
      description: 'Search process has been stopped', 
      variant: 'default' 
    });
  };

  // Search online images with streaming results
  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      toast({ title: 'Error', description: 'Please enter a search term', variant: 'destructive' });
      return;
    }

    setIsSearching(true);
    setSearchResults([]);
    
    // Reset stop processing flag for new search
    setShouldStopProcessing(false);
    stopProcessingRef.current = false;
    
    // Reset notification flag for new search
    hasShownCompatibleNotification.current = false;

    try {
      console.log('Searching for images:', searchTerm);
      
      const response = await fetch(`${baseUrl}/api/ProductImageWebSearch?searchtxt=${encodeURIComponent(searchTerm)}`, {
        headers: {
          'Authorization': authToken,
        },
      });

      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`);
      }

      const data = await response.json();
      console.log('Search API response:', data);

      if (data.Links && Array.isArray(data.Links)) {
        console.log(`Found ${data.Links.length} images from search API`);
        
        // Show initial processing message
        toast({ 
          title: 'Processing', 
          description: `Found ${data.Links.length} images, checking compatibility...`, 
          variant: 'default' 
        });
        
        // Set filtering state
        setIsFiltering(true);
        
        const compatibleCount = await streamCompatibleImages(data.Links, handleImageFound);
        
        // Clear filtering state
        setIsFiltering(false);
        
        if (compatibleCount === 0) {
          toast({ 
            title: 'No Results', 
            description: 'No compatible images found. Try a different search term.', 
            variant: 'destructive' 
          });
        } else {
          const message = shouldStopProcessing 
            ? `Found ${compatibleCount} compatible images (processing stopped early)`
            : `Found ${compatibleCount} compatible images`;
            
          toast({ 
            title: shouldStopProcessing ? 'Processing Stopped' : 'Search Complete', 
            description: message, 
            variant: 'default' 
          });
        }
      } else {
        console.log('No images found in search results');
        toast({ title: 'No Results', description: 'No images found for this search term', variant: 'destructive' });
      }
    } catch (error) {
      console.error('Search error:', error);
      toast({ title: 'Error', description: 'Failed to search for images', variant: 'destructive' });
    } finally {
      setIsSearching(false);
    }
  };

  // Handle online image selection with retry logic
  const handleOnlineImageSelect = async (imageUrl: string) => {
    // Add loading state for this specific image
    setLoadingImages(prev => new Set(prev).add(imageUrl));
    
    try {
      console.log('Selected image URL:', imageUrl);
      
      // Since images are pre-filtered, this should work on first attempt
      console.log('Fetching image from URL to convert to File object...');
      
      // Fetch the image from the URL
      const imageResponse = await fetch(imageUrl);
      if (!imageResponse.ok) {
        throw new Error(`Failed to fetch image: ${imageResponse.status}`);
      }

      // Get the image blob
      const imageBlob = await imageResponse.blob();
      console.log('Image blob fetched:', imageBlob.size, 'bytes');
      console.log('Image blob type:', imageBlob.type);

      // Test image compatibility (should pass since pre-filtered)
      const compatibility = testImageCompatibility(imageBlob, imageUrl);
      if (!compatibility.isCompatible) {
        console.log('Image compatibility issues:', compatibility.issues);
        toast({ 
          title: 'Warning', 
          description: compatibility.warnings, 
          variant: 'default' 
        });
      }

      // Convert blob to File object
      const fileName = imageUrl.split('/').pop() || 'image.jpg';
      const cleanFileName = fileName.split('?')[0];
      const file = new File([imageBlob], cleanFileName, { type: imageBlob.type });
      console.log('File object created:', file.name, file.size, 'bytes');

      // Single API call (should work since pre-tested)
      console.log('Sending to getWebURL API (pre-tested, should work)...');
      
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${baseUrl}/api/getWebURL`, {
        method: 'POST',
        headers: {
          'Authorization': authToken,
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`getWebURL failed: ${response.status}`);
      }

      const data = await response.json();
      console.log('getWebURL API response:', data);

      if (!data.url || data.url === '') {
        throw new Error('API returned empty URL');
      }

      // Success! Toggle selection
      setSelectedOnlineImages(prev => 
        prev.includes(imageUrl) 
          ? prev.filter(url => url !== imageUrl)
          : [...prev, imageUrl]
      );
      
      // If this image was primary, clear primary selection when deselected
      if (primaryOnlineImage === imageUrl) {
        setPrimaryOnlineImage(null);
        toast({ 
          title: 'Primary Image Removed', 
          description: 'Primary image was deselected.', 
          variant: 'default' 
        });
      }
      

      
    } catch (error) {
      console.error('getWebURL error:', error);
      toast({ 
        title: 'Error', 
        description: `Failed to process image: ${error.message}`, 
        variant: 'destructive' 
      });
    } finally {
      // Remove loading state for this image
      setLoadingImages(prev => {
        const newSet = new Set(prev);
        newSet.delete(imageUrl);
        return newSet;
      });
    }
  };

  // Move selected online images to offline section
  const handleMoveToOffline = () => {
    const newImages: ImageItem[] = selectedOnlineImages.map((url, index) => ({
      id: `online-${Date.now()}-${index}`,
      url,
      name: `Online Image ${index + 1}`,
      isSelected: false,
      isPrimary: primaryOnlineImage === url, // Use primaryOnlineImage state
      isLocal: false
    }));

    // Sync primary selection from online to offline
    const syncedImages = syncPrimaryFromOnlineToOffline(newImages);
    
    setOfflineImages(prev => [...prev, ...syncedImages]);
    setSelectedOnlineImages([]);
    setPrimaryOnlineImage(null); // Clear primary selection in online
    setActiveSection('offline');
  };

  // Test server API functionality
  const testServerAPI = async (file: File) => {
    try {
      console.log('🧪 Testing server API with file:', file.name);
      
      const testFormData = new FormData();
      testFormData.append('file', file);
      testFormData.append('productCode', 'test');
      testFormData.append('productName', 'test');
      
      const testResponse = await fetch(`${baseUrl}/api/getWebURL`, {
        method: 'POST',
        headers: {
          'Authorization': authToken,
        },
        body: testFormData
      });
      
      console.log('🧪 Test API Response status:', testResponse.status);
      
      if (testResponse.ok) {
        const testData = await testResponse.json();
        console.log('🧪 Test API Response data:', testData);
        
        if (testData.url && !testData.url.startsWith('data:')) {
          console.log('✅ Server API is working correctly');
          return true;
        } else {
          console.log('❌ Server API is returning invalid URLs');
          return false;
        }
      } else {
        console.log('❌ Server API test failed:', testResponse.status);
        return false;
      }
    } catch (error) {
      console.log('❌ Server API test error:', error);
      return false;
    }
  };

  // Handle local file upload
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLocalFile(file);
      const reader = new FileReader();
      reader.onload = (ev) => setLocalFilePreview(ev.target?.result as string);
      reader.readAsDataURL(file);
      
      // Test the server API with this file
      testServerAPI(file);
    }
  };

  // Add local file to offline images
  const handleAddLocalFile = async () => {
    if (!localFilePreview || !localFile) {
      console.log('No file to upload:', { localFilePreview: !!localFilePreview, localFile: !!localFile });
      return;
    }

    // Set uploading state
    setIsUploading(true);

    try {
      console.log('Uploading local file:', localFile.name);
      console.log('File preview available:', !!localFilePreview);
      console.log('File size:', localFile.size, 'bytes');
      console.log('File type:', localFile.type);
      
      // Call getWebURL API for local file upload
      const formData = new FormData();
      formData.append('file', localFile);
      formData.append('productCode', productCode?.toString() || '');
      formData.append('productName', productName);

      console.log('FormData contents:');
      for (const [key, value] of formData.entries()) {
        if (value instanceof File) {
          console.log(`  ${key}: File(${value.name}, ${value.size} bytes, ${value.type})`);
        } else {
          console.log(`  ${key}: ${value}`);
        }
      }

      const response = await fetch(`${baseUrl}/api/getWebURL`, {
        method: 'POST',
        headers: {
          'Authorization': authToken,
        },
        body: formData
      });

      console.log('API Response status:', response.status);
      console.log('API Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error response:', errorText);
        throw new Error(`Upload failed: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('Local file upload API response:', data);
      console.log('Uploaded file URL:', data.url);

      // Require a valid server URL from getWebURL — no fallback to local preview
      const hasValidUrl = data.url && typeof data.url === 'string' && data.url.trim() !== '' && !data.url.startsWith('data:');
      if (!hasValidUrl) {
        toast({
          title: 'Upload failed',
          description: 'Please upload the image again. Unable to get the URL from server.',
          variant: 'destructive',
        });
        return;
      }

      const imageUrl = data.url.trim();
      console.log('✅ Using server URL from API:', imageUrl);

      const newImage: ImageItem = {
        id: `local-${Date.now()}`,
        url: imageUrl, // Use server URL for database storage
        serverUrl: data.url || '', // Store server URL for reference
        name: localFile.name,
        isSelected: false,
        isPrimary: false,
        isLocal: true,
        file: localFile,
        localPreview: localFilePreview // Store local preview as fallback
      };

      console.log('Adding new image to offline images:', {
        id: newImage.id,
        name: newImage.name,
        displayUrl: imageUrl.substring(0, 100) + '...',
        serverUrl: data.url ? data.url.substring(0, 100) + '...' : 'none',
        isDataUrl: imageUrl.startsWith('data:')
      });

      setOfflineImages(prev => [...prev, newImage]);
      setLocalFile(null);
      setLocalFilePreview(null);
      
      toast({ title: 'Success', description: 'File uploaded successfully' });
      
      // Note: We use localPreview for display to avoid 404 when CDN URL is not yet available.
      // Server URL is still stored and submitted to the API.
    } catch (error) {
      console.error('Upload error:', error);
      toast({ title: 'Upload Error', description: 'Failed to upload file', variant: 'destructive' });
    } finally {
      // Clear uploading state
      setIsUploading(false);
    }
  };

  // Handle offline image selection for deletion
  const handleOfflineImageSelect = (imageId: string) => {
    setImagesToDelete(prev => 
      prev.includes(imageId) 
        ? prev.filter(id => id !== imageId)
        : [...prev, imageId]
    );
  };

  // Delete selected offline images
  const handleDeleteOfflineImages = () => {
    // Check if primary image is being deleted
    const isPrimaryBeingDeleted = imagesToDelete.includes(primaryOfflineImage || '');
    
    setOfflineImages(prev => prev.filter(img => !imagesToDelete.includes(img.id)));
    setImagesToDelete([]);
    setShowDeleteCheckboxes(false);
    
    // Clear primary selection if primary image was deleted
    if (isPrimaryBeingDeleted) {
      setPrimaryOfflineImage(null);
      toast({ 
        title: 'Primary Image Removed', 
        description: 'Primary image was deleted. Please select a new primary image.', 
        variant: 'default' 
      });
    }
  };

  // Delete a single offline image via icon (toast confirm on mobile)
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [pendingDeleteTimestamp, setPendingDeleteTimestamp] = useState<number>(0);

  const performDeleteImage = (imageId: string) => {
    const image = offlineImages.find(i => i.id === imageId);
    if (!image) return;
    const isPrimaryBeingDeleted = primaryOfflineImage === imageId;
    setOfflineImages(prev => prev.filter(i => i.id !== imageId));
    setImagesToDelete(prev => prev.filter(id => id !== imageId));
    if (isPrimaryBeingDeleted) {
      setPrimaryOfflineImage(null);
      toast({ title: 'Primary Image Removed', description: 'Primary image was deleted. Please select a new primary image.' });
    }
    toast({ title: 'Deleted', description: `${image.name} removed` });
  };

  const handleDeleteSingleOfflineImage = (imageId: string) => {
    setOfflineDeleteId(imageId);
    setShowOfflineDeleteConfirm(true);
  };

  // Validate submission data
  const validateSubmission = () => {
    if (!productCode) {
      toast({ title: 'Error', description: 'Product code is missing', variant: 'destructive' });
      return false;
    }

    if (offlineImages.length === 0) {
      toast({ title: 'Error', description: 'No images to submit', variant: 'destructive' });
      return false;
    }

    // Check if all images have valid URLs
    const invalidImages = offlineImages.filter(img => !img.url || img.url.trim() === '');
    if (invalidImages.length > 0) {
      toast({ title: 'Error', description: 'Some images have invalid URLs', variant: 'destructive' });
      return false;
    }

    // Check if multiple images are marked as primary
    const primaryImages = offlineImages.filter(img => img.isPrimary);
    if (primaryImages.length > 1) {
      toast({ title: 'Error', description: 'Only one image can be marked as primary', variant: 'destructive' });
      return false;
    }

    return true;
  };

  // Handle final submission
  const handleSubmit = async () => {
    if (!validateSubmission()) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Prepare request payload
      const productImagesRequest: ProductImagesRequest = {
        ProductCode: productCode,
        images: offlineImages.map(img => ({
          imageurl: img.url,
          isPrimary: img.isPrimary ? 1 : 0
        }))
      };

      // Log request payload before submission
      console.log('=== PRODUCT IMAGES API REQUEST ===');
      console.log('Request URL:', `${baseUrl}/api/ProductImages`);
      console.log('Request Method:', 'POST');
      console.log('Request Headers:', {
        'Authorization': 'Bearer [TOKEN]',
        'Content-Type': 'application/json'
      });
      console.log('Offline Images Details:');
      offlineImages.forEach((img, index) => {
        console.log(`  Image ${index + 1}:`, {
          id: img.id,
          name: img.name,
          url: img.url,
          isLocal: img.isLocal,
          isPrimary: img.isPrimary
        });
      });
      console.log('Request Body:', JSON.stringify(productImagesRequest, null, 2));
      console.log('=== END REQUEST ===');

      const response = await fetch(`${baseUrl}/api/ProductImages`, {
        method: 'POST',
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(productImagesRequest)
      });

      // Log response
      console.log('=== PRODUCT IMAGES API RESPONSE ===');
      console.log('Response Status:', response.status);
      console.log('Response Headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response Error Body:', errorText);
        console.log('=== END RESPONSE ===');
        throw new Error(`Product Images submission failed: ${response.status} - ${errorText}`);
      }

      const data: ProductImagesResponse = await response.json();
      console.log('Response Body:', JSON.stringify(data, null, 2));
      console.log('=== END RESPONSE ===');

      // Handle response based on status
      if (data.status === 0) {
        toast({ 
          title: 'Success', 
          description: data.message || 'Product images added successfully' 
        });
        navigateBackToInventory();
      } else {
        toast({ 
          title: 'Error', 
          description: data.message || 'Failed to upload images', 
          variant: 'destructive' 
        });
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast({ 
        title: 'Error', 
        description: error instanceof Error ? error.message : 'Failed to submit images', 
        variant: 'destructive' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigateBackToInventory();
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={navigateBackToInventory} className="flex-shrink-0">
                &#8592; Back
              </Button>
              <h1 className="text-2xl font-bold text-gray-900">Upload Images</h1>
            </div>
            {/* Mobile fixed submit/cancel bar: visible when image clicked or a primary image is selected */}
            {(showMobileSubmitBar || offlineImages.some(img => img.isPrimary)) && (
              <div className="sm:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 p-3 flex gap-2">
                <Button onClick={handleSubmit} disabled={offlineImages.length === 0 || isSubmitting} className="flex-1">
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit'
                  )}
                </Button>
                <Button variant="outline" onClick={handleCancel} className="flex-1">
                  Cancel
                </Button>
              </div>
            )}
          </div>
          <p className="text-gray-600">Product: {productName}</p>
        </div>

        {/* Toggle Buttons */}
        <div className="flex bg-white rounded-lg p-1 mb-6 shadow-sm">
          <button
            onClick={() => setActiveSection('online')}
            className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors ${
              activeSection === 'online'
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Online
          </button>
          <button
            onClick={() => setActiveSection('offline')}
            className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors ${
              activeSection === 'offline'
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Offline
          </button>
      </div>

        {/* Online Section */}
        {activeSection === 'online' && (
          <div className="bg-white rounded-lg shadow-sm p-6 flex flex-col h-[calc(100vh-200px)]">
            
            {/* Fixed Search Area - Non-scrollable on Android */}
            <div className="flex-shrink-0">
              {/* Search Bar - Responsive Layout */}
              <div className="flex flex-col sm:flex-row gap-2 mb-2 sm:mb-4">
                <div className="flex-1 relative">
                  <Input
                    type="text"
                    placeholder="Search for images..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pr-10"
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => setSearchTerm('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      title="Clear search"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleSearch} disabled={isSearching || isFiltering}>
                    {isSearching ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Searching...
                      </>
                    ) : isFiltering ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Filtering...
                      </>
                    ) : (
                      <>
                        <Search className="mr-2 h-4 w-4" />
                        Search
                      </>
                    )}
                  </Button>
                  {(isSearching || isFiltering) && (
                    <Button 
                      variant="outline" 
                      onClick={handleStopSearch}
                      className="px-3"
                      title="Stop Search"
                    >
                      <Pause className="h-4 w-4 text-red-600" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Move to Offline Button */}
              {selectedOnlineImages.length > 0 && (
                <div className="mb-2 sm:mb-4">
                  <Button onClick={handleMoveToOffline} className="w-full">
                    <ArrowRight className="w-4 h-4 mr-2" />
                    UPLOAD IMAGES ({selectedOnlineImages.length} images)
                  </Button>
                </div>
              )}

              {/* Removed the selected images info banner for all screens */}
            </div>

            {/* Search Results Grid */}
            {isFiltering && !isSearching && (
              <div className="flex items-center justify-center py-8">
                <div className="text-center max-w-md">
                  <Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-600 mb-4" />
                  <p className="text-gray-600 font-medium mb-2">Filtering images for compatibility...</p>
                  
                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ 
                        width: `${filterProgress.total > 0 ? (filterProgress.processed / filterProgress.total) * 100 : 0}%` 
                      }}
                    ></div>
                  </div>
                  
                  {/* Progress Statistics */}
                  <div className="text-sm text-gray-500 space-y-1">
                    <p>Processed: {filterProgress.processed} / {filterProgress.total}</p>
                    <p>Compatible found: {filterProgress.compatible}</p>
                    {filterProgress.processed > 0 && (
                      <p className="text-blue-600 font-medium">
                        {((filterProgress.compatible / filterProgress.processed) * 100).toFixed(1)}% success rate
                      </p>
                    )}
                  </div>
                  
                  {/* Stop Processing Button */}
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShouldStopProcessing(true);
                      stopProcessingRef.current = true;
                      console.log('🛑 Stop button clicked - processing will stop soon');
                    }}
                    className="mt-4"
                    disabled={shouldStopProcessing}
                  >
                    {shouldStopProcessing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Stopping...
                      </>
                    ) : (
                      'Stop Processing'
                    )}
                  </Button>
                  
                  {/* Continue Processing Button (when stopped early) */}
                  {shouldStopProcessing && filterProgress.processed < filterProgress.total && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        resumeProcessing(remainingUrls);
                      }}
                      className="mt-2 ml-2"
                    >
                      Continue Processing ({remainingUrls.length} remaining)
                    </Button>
                  )}
                  

                </div>
              </div>
            )}
            
            {/* Scrollable Images Area - both mobile and desktop scrollable */}
            <div className="flex-1 overflow-y-auto">
              {/* Search Results Grid - Show even while filtering */}
              {searchResults.length > 0 && (
                <div className="mb-6">
                {/* Results Header */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Compatible Images ({searchResults.length})
                  </h3>
                  {isFiltering && !isSearching && (
                    <div className="flex items-center text-sm text-blue-600">
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      <span>Processing...</span>
                    </div>
                  )}
                </div>
                
                {/* Images Grid */}
                <div className={`grid ${getGridClasses()} gap-4`}>
                  {searchResults.map((imageUrl, index) => (
                    <div
                      key={index}
                      className={`relative rounded-lg overflow-hidden border-2 transition-all ${
                        loadingImages.has(imageUrl)
                          ? 'cursor-not-allowed opacity-75'
                          : 'cursor-pointer'
                      } ${
                        selectedOnlineImages.includes(imageUrl)
                          ? 'border-blue-500 ring-2 ring-blue-200'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <img
                        src={imageUrl}
                        alt={`Search result ${index + 1}`}
                        className={`w-full h-48 object-cover ${loadingImages.has(imageUrl) ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                        onClick={() => !loadingImages.has(imageUrl) && handleOnlineImageSelect(imageUrl)}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                      
                      {/* Loading Overlay */}
                      {loadingImages.has(imageUrl) && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                          <div className="bg-white rounded-lg p-3 flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                            <span className="text-sm font-medium text-gray-700">Processing...</span>
                          </div>
                        </div>
                      )}
                      
                      {/* Selection and Primary Controls */}
                      <div className="absolute top-2 right-2 flex gap-2">
                        {selectedOnlineImages.includes(imageUrl) && (
                          <div className="bg-blue-500 text-white rounded-full p-1">
                            <Check className="w-4 h-4" />
                          </div>
                        )}
                        
                        {/* Primary Image Checkbox */}
                        {selectedOnlineImages.includes(imageUrl) && (
                          <div 
                            className="bg-white rounded-full p-1 cursor-pointer hover:bg-gray-100"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePrimaryOnlineChange(imageUrl);
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={primaryOnlineImage === imageUrl}
                              onChange={() => handlePrimaryOnlineChange(imageUrl)}
                              className="w-3 h-3 text-blue-600 rounded focus:ring-blue-500"
                            />
                          </div>
                        )}
                      </div>
                      
                      {/* Primary Badge */}
                      {primaryOnlineImage === imageUrl && (
                        <div className="absolute bottom-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded">
                          Primary
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            </div>
          </div>
        )}

        {/* Offline Section */}
        {activeSection === 'offline' && (
          <div className="bg-white rounded-lg shadow-sm p-6 flex flex-col h-[calc(100vh-200px)]">
            
            {/* Fixed Upload Area - Non-scrollable on Android */}
            <div className="flex-shrink-0">
              {/* Upload Section - Mobile condensed row */}
              <div className="sm:hidden flex items-center gap-2 mb-2">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="w-[130px]"
                  disabled={isUploading}
                />
                <Button onClick={handleAddLocalFile} disabled={!localFilePreview || isUploading} size="icon" className="shrink-0">
                  {isUploading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Upload className="h-5 w-5" />
                  )}
                </Button>
                {/* Mobile: direct View Images icon */}
                <Button variant="outline" size="icon" aria-label="View Images" className="shrink-0" onClick={fetchProductImages}>
                  <ImageIcon className="h-5 w-5" />
                </Button>
              </div>

              {/* Upload Section - Desktop/web (unchanged) */}
              <div className={`hidden sm:flex ${getSearchLayout()} mb-6`}>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="flex-1"
                  disabled={isUploading}
                />
                <Button onClick={handleAddLocalFile} disabled={!localFilePreview || isUploading}>
                  {isUploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload
                    </>
                  )}
                </Button>
              </div>

              {/* Desktop inline row: count badge + actions */}
              <div className="hidden sm:flex items-center gap-2 mb-6">
                {offlineImages.length > 0 && (
                  <span className="inline-flex items-center px-3 py-1 text-sm rounded-md bg-green-50 border border-green-200 text-green-800">
                    {offlineImages.length} image(s) uploaded
                  </span>
                )}
                <Button
                  variant="outline"
                  onClick={fetchProductImages}
                  disabled={isLoadingView}
                >
                  {isLoadingView ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4 mr-2" />
                      View Images
                    </>
                  )}
                </Button>
                {(offlineImages.some(img => img.isPrimary) || showMobileSubmitBar) && (
                  <>
                    <Button onClick={handleSubmit} disabled={offlineImages.length === 0 || isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        'Submit'
                      )}
                    </Button>
                    <Button variant="outline" onClick={handleCancel}>Cancel</Button>
                  </>
                )}
              </div>

              {/* Action Buttons - Desktop, Kebab on Mobile */}
              <div className="mb-2 sm:mb-6 flex items-center justify-between"></div>

              {/* Final Action Buttons - moved to bottom bar on mobile */}
            </div>



            {/* Scrollable Images Area - both mobile and desktop scrollable */}
            <div className="flex-1 overflow-y-auto">
              {/* Offline Images Grid */}
              {offlineImages.length > 0 && (
                <div className={`grid ${getGridClasses()} gap-4 mb-6`}>
                {offlineImages.map((image) => (
                  <div
                    key={image.id}
                    className={`relative rounded-lg overflow-hidden border-2 ${
                      showDeleteCheckboxes && imagesToDelete.includes(image.id)
                        ? 'border-red-500 ring-2 ring-red-200'
                        : image.isPrimary
                        ? 'border-green-500 ring-2 ring-green-200'
                        : 'border-gray-200'
                    }`}
                    onClick={() => setShowMobileSubmitBar(true)}
                  >
                    {/* Delete Checkbox */}
                    {showDeleteCheckboxes && (
                      <div className="absolute top-2 left-2 z-10">
                        <input
                          type="checkbox"
                          checked={imagesToDelete.includes(image.id)}
                          onChange={() => handleOfflineImageSelect(image.id)}
                          className="w-4 h-4"
                        />
                      </div>
                    )}
                    
                    {/* Primary checkbox + delete icon */}
                    <div className="absolute top-2 right-2 z-10 flex gap-2 items-center">
                      <button
                        className="bg-white rounded-full p-1 shadow-sm active:scale-95"
                        onClick={(e) => { e.stopPropagation(); handleDeleteSingleOfflineImage(image.id); }}
                        aria-label="Delete image"
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </button>
                      <div 
                        className="bg-white rounded-full p-1 cursor-pointer hover:bg-gray-100 shadow-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePrimaryOfflineChange(image.id);
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={image.isPrimary}
                          onChange={() => handlePrimaryOfflineChange(image.id)}
                          className="w-3 h-3 text-green-600 rounded focus:ring-green-500"
                        />
                      </div>
                    </div>
                    
                    <div className="w-full h-48 bg-gray-100 flex items-center justify-center relative">
                      <img
                        src={
                          // Use local preview when available to avoid 404 from CDN (getWebURL returns URL but file may not be served yet)
                          image.localPreview && image.url.startsWith('http') && !image.url.startsWith('data:')
                            ? image.localPreview
                            : image.url
                        }
                        alt={image.name}
                        className="w-full h-full object-cover"
                        onLoad={() => console.log('Image loaded successfully:', image.name)}
                        onError={(e) => {
                          console.log('Image failed to load, using fallback:', image.name);
                          
                          // Check if this is a server URL that's returning 404
                          if (image.url.startsWith('http') && !image.url.startsWith('data:')) {
                            console.log('🔄 Server URL returned 404, trying fallback to local preview');
                            
                            // First try to use stored local preview
                            if (image.localPreview) {
                              e.currentTarget.src = image.localPreview;
                              console.log('✅ Using stored local preview as fallback');
                              return;
                            }
                            
                            // If we have a local file, try to use its preview
                            if (image.file && image.isLocal) {
                              const reader = new FileReader();
                              reader.onload = (ev) => {
                                if (ev.target?.result) {
                                  e.currentTarget.src = ev.target.result as string;
                                  console.log('✅ Using local file preview as fallback');
                                }
                              };
                              reader.readAsDataURL(image.file);
                              return;
                            }
                          }
                          
                          // Show fallback content with image name
                          e.currentTarget.style.display = 'none';
                          const fallback = document.createElement('div');
                          fallback.className = 'w-full h-full flex flex-col items-center justify-center text-gray-500 text-sm bg-gray-50';
                          fallback.innerHTML = `
                            <div class="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mb-2">
                              <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                              </svg>
                            </div>
                            <div class="text-xs text-center px-2">${image.name}</div>
                            <div class="text-xs text-center px-2 text-red-500">Server 404</div>
                          `;
                          e.currentTarget.parentNode?.appendChild(fallback);
                        }}
                        crossOrigin="anonymous"
                      />
                    </div>
                    
                    {/* Primary Badge */}
                    {image.isPrimary && (
                      <div className="absolute bottom-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded">
                        Primary
                      </div>
                    )}
                    
                    <div className="p-2 bg-gray-50">
                      <p className="text-sm text-gray-600 truncate">{image.name}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {image.isLocal ? 'Local Upload' : 'Online Search'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Submission Summary */}
            {offlineImages.length > 0 && (
              <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Submission Summary</h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>• Total Images: {offlineImages.length}</p>
                  <p>• Primary Image: {offlineImages.find(img => img.isPrimary)?.name || 'None selected'}</p>
                  <p>• Product Name: {productName}</p>
                </div>
              </div>
            )}

            {/* Removed duplicate desktop action buttons (now in header row) */}
            <div className="sm:hidden h-16" />
            </div>
          </div>
        )}

        {/* View Images Popup */}
        {showViewPopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden animate-in fade-in-0 zoom-in-95 duration-200">
              {/* Popup Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Product Images</h3>
                  <p className="text-sm text-gray-500">Product: {productName}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowViewPopup(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Popup Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                {viewError ? (
                  <div className="text-center py-8">
                    <div className="text-red-500 text-lg font-medium mb-2">Error</div>
                    <div className="text-gray-600">{viewError}</div>
                  </div>
                ) : viewImages.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                      </svg>
                    </div>
                    <div className="text-gray-500 text-lg font-medium mb-2">No Images Available</div>
                    <div className="text-gray-400">No images have been uploaded for this product yet.</div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {viewImages.map((image) => (
                      <div
                        key={image.ProductImageId}
                        className={`relative rounded-lg overflow-hidden border-2 ${
                          image.isPrimary
                            ? 'border-green-500 ring-2 ring-green-200'
                            : 'border-gray-200'
                        }`}
                      >
                        {/* Primary Badge */}
                        {image.isPrimary && (
                          <div className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded z-10">
                            Primary
                          </div>
                        )}

                        {/* Image */}
                        <div className="w-full h-48 bg-gray-100 flex items-center justify-center relative">
                          <img
                            src={image.ImageUrl}
                            alt={`Product Image ${image.ProductImageId}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              const fallback = document.createElement('div');
                              fallback.className = 'w-full h-full flex flex-col items-center justify-center text-gray-500 text-sm bg-gray-50';
                              fallback.innerHTML = `
                                <div class="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mb-2">
                                  <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                                  </svg>
                                </div>
                                <div class="text-xs text-center px-2">Image ${image.ProductImageId}</div>
                                <div class="text-xs text-center px-2 text-red-500">Failed to load</div>
                              `;
                              e.currentTarget.parentNode?.appendChild(fallback);
                            }}
                          />
                        </div>

                        {/* Image Info */}
                        <div className="p-3 bg-gray-50">
                          <p className="text-xs text-gray-400">
                            {image.isPrimary ? 'Primary Image' : 'Secondary Image'}
                          </p>
                        </div>

                        {/* Action Buttons */}
                        <div className="p-3 bg-white border-t border-gray-200">
                          <div className="flex gap-2">
                            {/* Show Update button only for non-primary images */}
                            {!image.isPrimary && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUpdateImage(image)}
                                className="flex-1"
                              >
                                Set Primary
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteImage(image)}
                              className={image.isPrimary ? "w-full" : "flex-1"}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Popup Footer */}
              <div className="flex justify-end p-6 border-t border-gray-200">
                <Button
                  variant="outline"
                  onClick={() => setShowViewPopup(false)}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Popup */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full animate-in fade-in-0 zoom-in-95 duration-200">
              {/* Popup Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Confirm Delete</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={cancelDelete}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Popup Content */}
              <div className="p-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                    </svg>
                  </div>
                  <h4 className="text-lg font-medium text-gray-900 mb-2">Delete Image</h4>
                  <p className="text-gray-600 mb-6">
                    Are you sure you want to delete this image? This action cannot be undone.
                  </p>
                </div>
              </div>

              {/* Popup Footer */}
              <div className="flex gap-3 p-6 border-t border-gray-200">
                <Button
                  variant="outline"
                  onClick={cancelDelete}
                  className="flex-1"
                >
                  No, Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={confirmDelete}
                  className="flex-1"
                >
                  Yes, Delete
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Offline Delete Confirmation Popup */}
        {showOfflineDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full animate-in fade-in-0 zoom-in-95 duration-200">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirm delete</h3>
                <p className="text-gray-600 mb-6">Are you sure you want to delete?</p>
                <div className="flex gap-3 justify-end">
                  <Button variant="outline" onClick={() => { setShowOfflineDeleteConfirm(false); setOfflineDeleteId(null); }}>
                    No
                  </Button>
                  <Button variant="destructive" onClick={() => { if (offlineDeleteId) performDeleteImage(offlineDeleteId); setShowOfflineDeleteConfirm(false); setOfflineDeleteId(null); }}>
                    Yes
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadImage; 